import { Box, Paper, Stack, Typography } from '@mui/material';
import { groupPublicUrl } from '../../../services/groups-api-client';
import { buildGuestPreview } from '../../../lib/changeNoticePreview';
import type { GroupDetail, GroupInvitee } from '../../../types/groups';

/**
 * "What guests will see" — the draft with {name}, {title}, {location} and {url}
 * already filled in, for one representative guest.
 *
 * The API substitutes those per guest at send time, so the editable fields above
 * hold the literal text "{url}" and a host cannot tell what actually arrives. This
 * is read-only and never sent; it re-renders on every keystroke because it is
 * pure. The rendering lives in lib/changeNoticePreview.ts.
 */
export default function GuestPreviewPanel({
  group,
  subject,
  emailBody,
  smsBody,
  emailSelected,
  smsSelected,
  personalizeInviteLinks,
  guest: guestOverride,
}: {
  group: GroupDetail;
  subject: string;
  emailBody: string;
  smsBody: string;
  emailSelected: boolean;
  smsSelected: boolean;
  personalizeInviteLinks: boolean;
  /**
   * The guest to render the preview for when the audience is narrower than the
   * roster. `undefined` means all guests, so the first one on the roster; `null`
   * means a narrowed audience whose first guest is not on this roster copy.
   */
  guest?: GroupInvitee | null;
}) {
  // By default the audience is "all guests" (see buildRequest in
  // ChangeNoticeDialog), so the first guest on the roster is the first one this
  // goes to. A narrowed audience (a dinner's claimants) names its own.
  const guest = guestOverride === undefined ? (group.invitees ?? [])[0] ?? null : guestOverride;
  const preview = buildGuestPreview({
    subject,
    emailBody,
    smsBody,
    groupTitle: group.title,
    groupLocation: group.locationName,
    groupSlug: group.slug,
    guest,
    personalizeInviteLinks,
    publicUrl: groupPublicUrl,
  });

  const box = { p: 1.5, borderRadius: 2, bgcolor: 'action.hover', whiteSpace: 'pre-wrap', wordBreak: 'break-word' } as const;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 2 }} aria-label="What guests will see">
      <Typography variant="subtitle2" fontWeight={700}>What guests will see</Typography>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5 }}>
        Shown for {preview.guestName}
        {preview.guestNameIsPlaceholder ? ' (an example name — this trip has no guests yet)' : ''}.
        Each guest gets their own name{preview.urlIsPerGuest ? ' and their own link' : ''}.
      </Typography>
      <Stack spacing={1.5}>
        {emailSelected ? (
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary">Email</Typography>
            <Paper elevation={0} sx={box}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>{preview.subject}</Typography>
              <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>{preview.email}</Typography>
            </Paper>
          </Box>
        ) : null}
        {smsSelected ? (
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary">Text message</Typography>
            <Paper elevation={0} sx={box}>
              <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>{preview.sms}</Typography>
            </Paper>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
              The concierge puts a heading with the trip name above this and the trip link below it.
            </Typography>
          </Box>
        ) : null}
        <Typography variant="caption" color="text.secondary" component="p" sx={{ wordBreak: 'break-all' }}>
          {preview.urlIsPerGuest
            ? `Link for ${preview.guestName}: ${preview.url} — every guest gets their own, so it signs them in as themselves.`
            : `Link: ${preview.url} — the same shared link for every guest.`}
        </Typography>
      </Stack>
    </Paper>
  );
}
